<template>
  <v-content>
    <the-loading-overlay :value="loading" />

    <v-dialog
      max-width="1000px"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      v-model="previewDialogOpened"
      @keydown.esc="
        previewDialogOpened = false;
        documentPreviewData = '';
        previewRotation = 0;
      "
    >
      <v-card v-if="selectedDocumentLink && previewType">
        <v-card-title class="title">
          <span class="text-truncate" style="max-width: 600px">{{
            getDocumentName(selectedDocumentLink)
          }}</span>

          <v-spacer />
          <v-tooltip bottom class="mr-1">
            <v-btn
              fab
              small
              flat
              class="white primary--text text-capitalize ma-0"
              @click="previewRotation += 90"
              slot="activator"
            >
              <v-icon size="25"> rotate_right </v-icon>
            </v-btn>
            <span>Rotate</span>
          </v-tooltip>
          <v-btn
            fab
            small
            flat
            class="white error--text text-capitalize ma-0 ml-1"
            @click="
              previewDialogOpened = false;
              documentPreviewData = '';
              previewRotation = 0;
            "
          >
            <v-icon size="25"> clear </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="documentPreviewData && previewLoaded">
          <preview-document
            :document-preview-data="documentPreviewData"
            :preview-type="previewType"
            :preview-rotation="previewRotation"
          />
        </v-card-text>
        <v-card-text v-else>
          <v-progress-circular
            indeterminate
            size="50"
            class="primary--text ma-5"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <UploadDialog
      :opened.sync="uploadDialogOpened"
      :selected-question="selectedQuestion"
      :documents-to-import="documentsToImport"
      @select-document-type="selectDocumentType"
      @pick-documents="pickDocumentsForSingleUpload()"
      @import-documents="uploadSingleFiles()"
      @add-file="addSingleFile"
    />

    <DocumentDropOffCompletionDialog
      :opened.sync="documentDropOffCompletionDialogOpened"
      @go-to-dashboard="goToDashboard"
      :current-questionnaire="currentQuestionnaire"
      :user-signed-in="userSignedIn"
      :current-user="currentUser"
    />

    <ConfirmationDialog v-model="signInConfirmationDialog" @confirm="signIn">
      You must sign-in to preview this document. Do you want to proceed?
    </ConfirmationDialog>

    <input
      type="file"
      style="display: none"
      ref="selectedDocuments"
      multiple
      @change="onDocumentsPicked"
    />

    <input
      type="file"
      style="display: none"
      ref="selectedDocumentsForSingleUpload"
      multiple
      @change="handleDocumentsPickedForSingleUpload"
    />

    <QuestionnaireCompletedConfirmationDialog
      :opened.sync="questionnaireCompletedConfirmationDialogOpened"
      :submission-agreed="true"
      :questionnaire="currentQuestionnaire"
      :current-questionnaire="currentQuestionnaire"
      :documents-still-needed="[]"
      @confirm="completeQuestionnaire()"
    />

    <div id="recaptcha-container" />
    <v-layout align-center justify-center>
      <v-flex xs12 md8 lg6 class="text-xs-left">
        <v-btn
          class="text-none primary--text"
          @click="goToDashboard"
          flat
          v-if="userSignedIn"
        >
          <v-icon color="primary" size="18"> keyboard_arrow_left </v-icon>
          {{ $t('questionnaire.backToDashboard') }}
        </v-btn>
        <v-layout align-end>
          <h2 class="section-title mb-2">
            {{ currentQuestionnaire.tax_year }} {{ currentQuestionnaire.name }}
          </h2>
          <v-spacer />
          <span v-if="userSignedIn" class="primary--text caption"
            >Signed In</span
          >
        </v-layout>

        <v-alert outline :value="true" type="info" class="text-xs-left">
          Please drop off any documents related for the bottom checklist. If the
          document was not recognized correctly, you can drag the document to
          the correct request to mark it completed.
        </v-alert>

        <v-layout class="mt-3 mb-4" wrap>
          <v-btn
            block
            @click="pickDocuments"
            :loading="documentUploading"
            class="primary hidden-sm-and-up text-none mb-3"
          >
            Choose Files
          </v-btn>

          <v-flex
            @drop.prevent="addFile"
            @dragover.prevent
            class="hidden-xs-only"
          >
            <v-layout class="drag-and-drop" style="min-height: 200px">
              <v-layout column align-center justify-center v-if="uploading">
                <v-progress-circular
                  indeterminate
                  size="40"
                  class="primary--text ma-5"
                />
              </v-layout>
              <v-layout column align-center justify-center v-else>
                <div>
                  <v-icon>upload_file</v-icon>
                </div>
                <div>
                  <span @click="pickDocuments" class="drag-and-drop-text"
                    >Drag and drop files here or
                    <span class="clickable primary--text">browse</span></span
                  >
                </div>
              </v-layout>
            </v-layout>
          </v-flex>
        </v-layout>
        <v-layout column align-start>
          <span class="subheading font-weight-bold"
            >Documents to upload, if applicable:</span
          >
          <v-progress-linear
            :indeterminate="documentChecklistLoading"
            class="mb-0"
            :style="documentChecklistLoading ? '' : 'visibility: hidden'"
          ></v-progress-linear>
          <v-layout column class="mb-2 subheading">
            <v-expansion-panel
              class="my-2 expansion-panel"
              expand
              v-model="active"
            >
              <v-expansion-panel-content
                v-for="question in currentQuestionnaire.documents_uploaded_and_needed"
                :key="question.id"
              >
                <template v-slot:header>
                  <v-layout row align-center>
                    <div class="pr-2">
                      <v-icon
                        v-if="!isEmpty(question.answer)"
                        large
                        color="success"
                      >
                        check_circle
                      </v-icon>

                      <v-icon
                        v-else
                        large
                        class="darkgrey--text text--lighten-2"
                        >radio_button_unchecked</v-icon
                      >
                    </div>

                    <div
                      class="document-item-text"
                      v-html="
                        !isEmpty(question.options)
                          ? question.options.join(', ')
                          : question.text
                      "
                    />
                  </v-layout>
                </template>

                <v-card>
                  <v-card-text class="pt-0">
                    <v-layout column class="px-2">
                      <v-layout
                        align-center
                        v-if="question.answer === 'Not Applicable'"
                      >
                        <span class="darkgrey--text text-xs-left subheading"
                          >This item was marked
                          <strong>not applicable</strong>.</span
                        >
                        <v-btn
                          @click.prevent.stop="saveQuestion(question, '')"
                          outline
                          small
                          color="primary"
                          class="ml-3 font-weight-bold text-capitalize"
                          >Undo</v-btn
                        >
                      </v-layout>

                      <v-layout
                        align-center
                        v-else-if="question.answer === 'Marked Completed'"
                      >
                        <span class="darkgrey--text text-xs-left subheading"
                          >This item was marked <b>completed</b>.</span
                        >
                        <v-btn
                          @click.prevent.stop="saveQuestion(question, '')"
                          outline
                          small
                          color="primary"
                          class="ml-3 font-weight-bold text-capitalize"
                          >Undo</v-btn
                        >
                      </v-layout>

                      <v-layout
                        align-center
                        v-else-if="
                          question.answer === 'Provided in Questionnaire'
                        "
                      >
                        <span class="darkgrey--text text-xs-left subheading"
                          >This item was marked provided in the
                          questionnaire.</span
                        >
                        <v-btn
                          @click.prevent.stop="saveQuestion(question, '')"
                          outline
                          small
                          color="primary"
                          class="ml-3 font-weight-bold text-capitalize"
                          >Undo</v-btn
                        >
                      </v-layout>

                      <v-layout
                        align-center
                        v-else-if="question.answer === 'Provided Elsewhere'"
                      >
                        <span class="darkgrey--text text-xs-left subheading"
                          >This item was marked <b>provided elsewhere</b>.</span
                        >
                        <v-btn
                          @click.prevent.stop="saveQuestion(question, '')"
                          outline
                          small
                          color="primary"
                          class="ml-3 font-weight-bold text-capitalize"
                          >Undo</v-btn
                        >
                      </v-layout>

                      <div v-else class="description mb-2 darkgrey--text">
                        <div
                          v-if="
                            question.section === 'Documents from Last Year' ||
                              question.section === 'Other Prior Year Documents'
                          "
                        >
                          <span
                            >This item is listed because the client uploaded the
                            same document last year.</span
                          >
                        </div>
                        <div v-else-if="question.follow_up_question_id">
                          <span
                            v-html="
                              formatReason(
                                question,
                                getParentQuestion(
                                  findSection(question),
                                  question,
                                ),
                              )
                            "
                          />
                        </div>
                      </div>
                    </v-layout>

                    <draggable
                      :value="listDocuments(question.answer)"
                      :animation="200"
                      ghost-class="ghost-card"
                      group="documents"
                      :data-question-id="question.id"
                      @end="reorderDocuments"
                    >
                      <v-layout
                        v-if="!isEmpty(question.answer)"
                        align-center
                        class="file-item mx-2 mb-2 px-3 py-2 text-xs-left body-1"
                        v-for="(document, i) in listDocuments(question.answer)"
                        :key="i"
                        @click="checkIfUserSignedInToPreviewDocument(document)"
                      >
                        <v-icon
                          color="grey darken-2"
                          class="mr-1 material-icons-outlined move-handle"
                        >
                          drag_handle
                        </v-icon>

                        <v-icon
                          color="grey darken-2"
                          class="mr-1 material-icons-outlined"
                        >
                          insert_drive_file
                        </v-icon>
                        <div class="mr-1">{{ getDocumentName(document) }}</div>
                        <v-layout
                          align-start
                          wrap
                          v-if="findExtractedDocument(document)"
                        >
                          <v-chip
                            small
                            v-if="
                              findExtractedDocument(document).detailed_name &&
                                currentUser.is_advisor
                            "
                            ><v-layout wrap>{{
                              findExtractedDocument(document).detailed_name
                            }}</v-layout></v-chip
                          >
                        </v-layout>
                        <v-spacer />
                        <v-btn
                          @click.stop.prevent="
                            deleteDocument(question, document)
                          "
                          style="cursor: pointer"
                          color="error"
                          flat
                          small
                          class="text-none"
                          :loading="
                            isDocumentBeingDeleted(question.id, document)
                          "
                        >
                          Remove
                        </v-btn>
                      </v-layout>
                    </draggable>

                    <v-layout
                      wrap
                      class="pt-2"
                      v-if="
                        ![
                          'Upload Later',
                          'Answer Later',
                          'Not Applicable',
                          'Marked Completed',
                          'Provided Elsewhere',
                        ].includes(question.answer)
                      "
                    >
                      <v-btn
                        color="primary"
                        class="text-none"
                        @click="openUploadDialog(question)"
                        :loading="questionsUploadingDocs.includes(question.id)"
                      >
                        Upload
                      </v-btn>

                      <v-btn
                        outline
                        color="primary"
                        class="text-none"
                        @click.prevent.stop="
                          saveQuestion(question, 'Not Applicable')
                        "
                      >
                        Not Applicable
                      </v-btn>

                      <v-btn
                        v-if="!disableProvidedElsewhere"
                        outline
                        color="primary"
                        class="text-none"
                        @click.prevent.stop="
                          saveQuestion(question, 'Provided Elsewhere')
                        "
                      >
                        Provided Elsewhere
                      </v-btn>
                    </v-layout>
                  </v-card-text>
                </v-card>
              </v-expansion-panel-content>

              <v-expansion-panel-content>
                <template v-slot:header>
                  <v-layout row align-center>
                    <div class="pr-2">
                      <v-icon
                        large
                        class="darkgrey--text text--lighten-1 material-icons-outlined"
                        >file_copy</v-icon
                      >
                    </div>
                    <span class="document-item-text"
                      >Additional Attachments</span
                    >
                    <v-chip class="ml-2" small>
                      {{
                        currentQuestionnaireOtherDocuments.length === 1
                          ? '1 document'
                          : currentQuestionnaireOtherDocuments.length +
                            ' documents'
                      }}
                    </v-chip>
                  </v-layout>
                </template>

                <v-card>
                  <v-card-text class="pt-0">
                    <draggable
                      :value="currentQuestionnaireOtherDocuments"
                      :animation="200"
                      ghost-class="ghost-card"
                      group="documents"
                      @end="reorderDocuments"
                    >
                      <v-layout
                        align-center
                        class="file-item mx-2 mb-2 px-3 py-2 text-xs-left body-1"
                        v-for="(document,
                        i) in currentQuestionnaireOtherDocuments"
                        :key="document.id"
                        @click="checkIfUserSignedInToPreviewDocument(document)"
                      >
                        <v-icon
                          color="grey darken-2"
                          class="mr-1 material-icons-outlined move-handle"
                        >
                          drag_handle
                        </v-icon>

                        <v-icon
                          color="grey darken-2"
                          class="mr-1 material-icons-outlined"
                        >
                          insert_drive_file
                        </v-icon>
                        {{ getDocumentName(document) }}
                        <v-spacer />

                        <v-btn
                          @click.stop.prevent="deleteDocument(null, document)"
                          style="cursor: pointer"
                          color="error"
                          flat
                          small
                          class="text-none"
                          :loading="isDocumentBeingDeleted('other', document)"
                        >
                          Remove
                        </v-btn>
                      </v-layout>

                      <v-layout wrap class="pt-2">
                        <v-btn
                          color="primary"
                          class="text-none"
                          @click="pickDocuments()"
                        >
                          Upload
                        </v-btn>
                      </v-layout>
                    </draggable>
                  </v-card-text>
                </v-card>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-layout>
        </v-layout>
        <v-btn
          block
          v-if="!currentQuestionnaire.completed"
          class="text-capitalize primary"
          :loading="completionLoading"
          :disabled="!allDocumentsUploaded"
          @click="questionnaireCompletedConfirmationDialogOpened = true"
        >
          {{ $t('questionnaire.complete') }}
        </v-btn>
      </v-flex>
    </v-layout>
  </v-content>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import jwt_decode from 'jwt-decode';
import API from '@/api';
import TheLoadingOverlay from '@/components/TheLoadingOverlay';
import draggable from 'vuedraggable';
import UploadDialog from '@/components/dialogs/UploadDialog';
import has from 'lodash/has';
import get from 'lodash/get';
// import posthog from 'posthog-js';
import { mapGetters } from 'vuex';

import QuestionnaireCompletedConfirmationDialog from '@/components/dialogs/QuestionnaireCompletedConfirmationDialog';
import ConfirmationDialog from '@/components/ConfirmationDialog';
import DocumentDropOffCompletionDialog from '@/components/dialogs/DocumentDropOffCompletionDialog';
import PreviewDocument from '../components/PreviewDocument.vue';

import QuestionnaireMixin from '@/mixins/QuestionnaireMixin';
import UtilsMixins from '@/mixins/UtilsMixins';
import DocumentMixin from '@/mixins/DocumentMixin';
import NewDocumentChecklistMixin from '@/mixins/NewDocumentChecklistMixin';
import { supportedExtensionsForPreview } from '@/constants/variables';

export default {
  components: {
    PreviewDocument,
    TheLoadingOverlay,
    QuestionnaireCompletedConfirmationDialog,
    ConfirmationDialog,
    DocumentDropOffCompletionDialog,
    draggable,
    UploadDialog,
  },

  mixins: [
    QuestionnaireMixin,
    NewDocumentChecklistMixin,
    UtilsMixins,
    DocumentMixin,
  ],

  data() {
    return {
      supportedExtensionsForPreview,
      previewDialogOpened: false,
      previewRotation: 0,
      selectedDocumentLink: null,
      documentDropOffCompletionDialogOpened: false,
      signInConfirmationDialog: false,
      uploading: false,
      currentQuestionnaire: null,
      loading: true,
      documentsUploaded: [],
      taxDocuments: [
        {
          name: 'Common',
          documents: [
            'W-2 (Wage Income)',
            '1099-DIV (Dividend Income)',
            '1099-INT (Dividend Income)',
            '1099-R (Retirement Distribution)',
            '1098 (Mortgage Interest)',
            'Charitable Contribution Receipts',
          ],
        },
        {
          name: 'Other tax documents',
          documents: [
            '1099-G (Other income)',
            'W-2G (Gambling Income)',
            'K-1 (Partnership or Shareholder Income)',
            'SSA-1099 (Social Security)',
            '1099-NEC (Self Employement Income)',
            '1099-K (Third Party Payments)',
            '1098-T (Tuition Statement)',
            '1095-A (Health Insurance Marketplace)',
            'Profit & Loss Statement',
          ],
        },
      ],
      firstStep: true,
      questionnaireCompletedConfirmationDialogOpened: false,
      completionLoading: false,
      questionsUploadingDocs: [],
      documentsToImport: [],
      uploadDialogOpened: false,
      selectedQuestion: null,
      nextQuestionnaireToWorkOnDialogOpened: false,
      documentChecklistLoading: false,
      active: [],
      currentCompany: null,
      documentPreviewData: '',
      previewType: '',
    };
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
    }),
    // questionnaireHeader() {

    // },

    documentsNeeded() {
      if (this.currentQuestionnaire) {
        return this.currentQuestionnaire.documents_needed.map(a => a[0][0]);
      }
    },
    token() {
      const params = new URLSearchParams(window.location.search);
      return params.get('token');
    },
    allDocumentsUploaded() {
      return this.currentQuestionnaire.documents_uploaded_and_needed.every(
        a => !isEmpty(a.answer),
      );
    },
    currentQuestionnaireOtherDocumentsCount() {
      return this.currentQuestionnaireOtherDocuments.length;
    },
    userSignedIn() {
      return !!this.currentUser.id;
    },
    currentCompanyId() {
      const decoded = jwt_decode(this.token);
      return decoded.company_id;
    },
    currentQuestionnaireId() {
      const decoded = jwt_decode(this.token);
      return decoded.questionnaire_id;
    },
    questionnaireLink() {
      const decoded = jwt_decode(this.token);
      if (decoded.email) {
        return `/questionnaire/?questionnaireId=${
          this.currentQuestionnaireId
        }&email=${decoded.email.replace('+', '%2b')}`;
      } else {
        return `/questionnaire/?questionnaireId=${this.currentQuestionnaireId}`;
      }
    },
  },

  methods: {
    isEmpty,
    moment,
    has,
    get,
    checkIfUserSignedInToPreviewDocument(document) {
      if (this.userSignedIn) {
        this.previewDocument(document);
      } else {
        this.signInConfirmationDialog = true;
      }
    },
    async previewDocument(documentLink) {
      this.previewType = '';
      this.previewLoaded = false;
      this.documentPreviewData = '';
      this.selectedDocumentLink = documentLink;
      this.previewRotation = 0;
      const response = await API.company.getCompany(this.currentCompanyId);
      if (response.data.error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'You do not have access to this entity.',
        });
      } else {
        this.currentCompany = response.data;
        this.previewType = this.endsWithAny(
          this.supportedExtensionsForPreview,
          documentLink,
        );
        if (this.previewType) {
          if (this.previewType === 'pdf') {
            this.currentPage = 1;
            this.pdfLoaded = false;
          }
          this.previewDialogOpened = true;
          try {
            const response = await API.integration.downloadDocument({
              companyId: this.currentCompanyId,
              link: this.currentCompany.upload_link.concat('/', documentLink),
            });

            if (response.data.document) {
              this.documentPreviewData = response.data.document.data.link;
            } else {
              console.log(response.data.error);
              this.$store.dispatch('application/setErrorNotification', {
                message: 'There was a problem downloading the document.',
                errorToSend: response.data.error,
              });
            }
          } catch (e) {
            console.log(e);
            this.$store.dispatch('application/setErrorNotification', {
              message: 'There was a problem downloading the document.',
              errorToSend: e,
            });
          } finally {
            this.previewLoaded = true;
          }
        } else {
          const response = await API.integration.downloadDocument({
            companyId: this.currentCompany.id,
            link: this.currentCompany.upload_link.concat('/', documentLink),
          });

          if (response.data.document) {
            this.documentPreviewData = response.data.document.data.link;
            this.downloadDocumentByLink(
              this.documentPreviewData,
              this.getDocumentName(documentLink),
            );
          } else {
            console.log(response.data.error);
            this.$store.dispatch('application/setErrorNotification', {
              message: 'There was a problem downloading the document.',
              errorToSend: response.data.error,
            });
          }
        }
      }
    },
    async downloadDocumentByLink(url, name) {
      const link = document.createElement('a');
      // If you don't know the name or want to use
      // the webserver default set name = ''
      link.setAttribute('download', name);
      link.target = '_blank';
      link.href = url;
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    async deleteDocument(question, document) {
      this.documentsDeleting.push(
        question ? question.id : 'other' + '-' + document,
      );

      try {
        const response = await API.documentDropOff.deletePath({
          path: '/'.concat(document),
          token: this.token,
        });

        if (response.data.error) {
          console.log(response.data.error);
        } else {
          if (question) {
            let newAnswer;

            if (question.answer.split('&!&').length === 1) {
              newAnswer = '';
            } else {
              newAnswer = question.answer
                .split('&!&')
                .filter(d => d !== document)
                .join('&!&');
            }

            this.saveQuestion(question, newAnswer);
          }
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'You removed the document.',
          });
        }
      } catch (e) {
        console.log(e);
      } finally {
        this.documentsDeleting = this.documentsDeleting.filter(
          id => id !== (question ? question.id : 'other' + '-' + document),
        );
      }
    },
    goToDashboard() {
      const decoded = jwt_decode(this.token);
      if (this.userSignedIn) {
        if (this.currentUser.is_advisor) {
          this.$router.push(`/admin-dashboard?companyId=${decoded.company_id}`);
        } else {
          this.$router.push(`/?companyId=${decoded.company_id}`);
        }
      } else {
        if (decoded.email) {
          this.$router.push(
            `/signin?email=${decoded.email.replace(
              '+',
              '%2b',
            )}&redirect=/?companyId=${decoded.company_id}`,
          );
        } else {
          this.$router.push(`/?companyId=${decoded.company_id}`);
        }
      }
    },
    expandAdditionalAttachments() {
      this.active = this.currentQuestionnaire.documents_uploaded_and_needed.length;
    },
    pickDocumentsForSingleUpload() {
      const input = this.$refs.selectedDocumentsForSingleUpload;
      input.type = 'text';
      input.type = 'file';
      this.$refs.selectedDocumentsForSingleUpload.click();
    },

    async saveQuestion(question, value) {
      question.answer = value;
      await API.documentDropOff.answerQuestion({
        id: question.id,
        answer: question.answer,
        answerBy: question.answer_by,
        token: this.token,
      });

      // posthog.capture('answered question', {
      //   questionnaire_id: this.currentQuestionnaire.id,
      //   question_id: question.id,
      //   question_type: question.question_type,
      // });
    },

    selectDocumentType(documentType) {
      this.selectedDocumentType = documentType;
    },

    async uploadSingleFiles(e) {
      const questionId = this.selectedQuestion.id;
      this.questionsUploadingDocs.push(questionId);

      let path;
      let answer =
        this.selectedQuestion.answer === 'Not Applicable' ||
        isEmpty(this.selectedQuestion.answer)
          ? ''
          : this.selectedQuestion.answer;
      if (isEmpty(this.currentQuestionnaire.document_path)) {
        path = '';
      } else {
        path = this.currentQuestionnaire.document_path
          .replace('{year}', this.currentQuestionnaire.tax_year)
          .replace('{questionnaire_name}', this.currentQuestionnaire.name);
      }
      try {
        this.uploadDialogOpened = false;

        for (const documentToImport of this.documentsToImport) {
          const trueExtension = await this.getTrueExtension(
            documentToImport.data,
          );
          const extensionsToCheck = ['pdf', 'png', 'gif', 'jpeg'];

          if (documentToImport.data.size === 0) {
            throw new Error(
              `We were unable to process some of uploaded files as they appear to be EMPTY. Kindly check those files and try uploading them again.`,
            );
          }

          if (
            extensionsToCheck.includes(documentToImport.extension) &&
            trueExtension !== documentToImport.extension
          ) {
            throw new Error(
              `We were unable to process some of uploaded files as they appear to be INVALID. Kindly check those files and try uploading them again.`,
            );
          }

          if (documentToImport.extension === 'pdf') {
            if (await this.pdfHasPassword(documentToImport.data)) {
              throw new Error(
                `The PDFs you uploaded are password-protected. Please either remove the password or upload alternative files.`,
              );
            }
          }

          const {
            data: { id, upload_link },
          } = await API.documentDropOff.getTemporaryUploadLink({
            token: this.token,
          });

          await API.integration.uploadToDropbox(
            upload_link,
            documentToImport.data,
          );

          const response = await API.documentDropOff.processDocument({
            id,
            documentName:
              documentToImport.name + '.' + documentToImport.extension,
            path: '/'.concat(path),
            questionnaireId: this.currentQuestionnaire.id,
            selectedOptions: documentToImport.documentType
              ? [documentToImport.documentType]
              : [],
            description: documentToImport.documentType
              ? documentToImport.documentType
              : '',
            tags: [this.selectedQuestion.section],
            token: this.token,
          });

          if (response.data.error) {
            console.log(response.data.error);
          } else if (response.data.is_duplicate) {
            this.$store.dispatch('application/setErrorNotification', {
              message: `The document ${documentToImport.name} has already been uploaded before. Thus, you don't need to upload it again.`,
            });
          } else {
            let list = response.data.document_location.split('/');
            list.splice(0, 3);
            answer += '&!&'.concat(list.join('/'));
          }
        }

        const question = this.currentQuestionnaire.documents_uploaded_and_needed.find(
          question => question.id === questionId,
        );
        question.answer = answer;

        this.documentsToImport = [];

        await API.documentDropOff.answerQuestion({
          id: questionId,
          answer: answer,
          token: this.token,
        });

        this.refreshQuestionnaireUntilDocumentsAreProcessed();
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: has(e, 'message')
            ? get(e, 'message')
            : 'There was a problem uploading the documents.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.markQuestionDocUploaded(questionId);
        this.documentChecklistLoading = true;
      }
    },

    async addSingleFile(e) {
      const documents = e.dataTransfer.files;
      if (documents) {
        const documentType = this.selectedDocumentType;
        this.documentsToImport.push(
          ...Array.from(documents).map(document => {
            return {
              name:
                documentType && !this.newDocumentManagementSystem
                  ? `${this.getNameWithoutExtensionOfFilename(
                      document.name,
                    )} (${documentType})`
                  : this.getNameWithoutExtensionOfFilename(document.name),
              data: document,
              extension: this.getExtensionOfFilename(document.name),
              documentType,
            };
          }),
        );
      }
    },

    async handleDocumentsPickedForSingleUpload(e) {
      const documents = e.target.files;
      if (documents) {
        const documentType = this.selectedDocumentType;
        this.documentsToImport.push(
          ...Array.from(documents).map(document => {
            return {
              name:
                documentType && !this.newDocumentManagementSystem
                  ? `${this.getNameWithoutExtensionOfFilename(
                      document.name,
                    )} (${documentType})`
                  : this.getNameWithoutExtensionOfFilename(document.name),
              data: document,
              extension: this.getExtensionOfFilename(document.name),
              documentType,
            };
          }),
        );
      }
    },

    markQuestionDocUploaded(questionId) {
      this.questionsUploadingDocs = this.questionsUploadingDocs.filter(
        id => id !== questionId,
      );
    },

    openUploadDialog(question) {
      if (!question) {
        this.uploadDialogOpened = true;
      }
      if (!this.questionHasDocumentsLoading(question.id)) {
        this.selectedQuestion = question;
        this.documentsToImport = [];
        this.selectedDocumentType =
          this.selectedQuestion.options.length > 1
            ? ''
            : this.selectedQuestion.options[0];
        this.uploadDialogOpened = true;
      }
    },

    questionHasDocumentsLoading(questionId) {
      return this.questionsUploadingDocs.includes(questionId);
    },

    async reorderDocuments(event) {
      const sourceQuestionId = event.from.hasAttribute('data-question-id')
        ? parseInt(event.from.getAttribute('data-question-id'))
        : null;

      const targetQuestionId = event.to.hasAttribute('data-question-id')
        ? parseInt(event.to.getAttribute('data-question-id'))
        : null;
      const oldIndex = event.oldIndex;
      const newIndex = event.newIndex;

      const sourceQuestion = sourceQuestionId
        ? this.currentQuestionnaire.documents_uploaded_and_needed.find(
            a => a.id === sourceQuestionId,
          )
        : null;
      const targetQuestion = targetQuestionId
        ? this.currentQuestionnaire.documents_uploaded_and_needed.find(
            a => a.id === targetQuestionId,
          )
        : null;

      if (!sourceQuestionId && !targetQuestionId) {
        // Case 0: Reordering documents within Additional Documents section
        // Currently, we don't need to support reordering documents in Additional Documents section
        // so we do nothing here
      }
      // Case 1: Moving a document from Additional Documents section to a upload question
      else if (!sourceQuestionId) {
        const targetDocument = this.currentQuestionnaireOtherDocuments[
          oldIndex
        ];

        const targetQuestionDocuments = this.listDocuments(
          targetQuestion.answer,
        );
        targetQuestionDocuments.splice(newIndex, 0, targetDocument);
        targetQuestion.answer = '&!&' + targetQuestionDocuments.join('&!&');

        const extractedDocument = this.currentQuestionnaire.extracted_documents.find(
          d => d.source.endsWith(targetDocument),
        );

        extractedDocument.marked_as_others = false;

        await Promise.all([
          API.documentDropOff.answerQuestion({
            id: targetQuestion.id,
            answer: targetQuestion.answer,
            token: this.token,
          }),
          API.documentDropOff.updateMarkedAsOthers({
            extractedDocumentId: extractedDocument.id,
            token: this.token,
            markedAsOthers: extractedDocument.marked_as_others,
          }),
        ]);
      }
      // Case 2: Moving a document from a upload question to Additional Documents section
      else if (!targetQuestionId) {
        const targetDocument = this.listDocuments(sourceQuestion.answer)[
          oldIndex
        ];

        const sourceQuestionDocuments = this.listDocuments(
          sourceQuestion.answer,
        ).filter(d => d !== targetDocument);

        sourceQuestion.answer =
          sourceQuestionDocuments.length === 0
            ? ''
            : '&!&' + sourceQuestionDocuments.join('&!&');

        const extractedDocument = this.currentQuestionnaire.extracted_documents.find(
          d => d.source.endsWith(targetDocument),
        );

        extractedDocument.marked_as_others = true;

        if (sourceQuestion) {
          await Promise.all([
            API.documentDropOff.answerQuestion({
              id: sourceQuestion.id,
              answer: sourceQuestion.answer,
              token: this.token,
            }),
            API.documentDropOff.updateMarkedAsOthers({
              extractedDocumentId: extractedDocument.id,
              token: this.token,
              markedAsOthers: extractedDocument.marked_as_others,
            }),
          ]);
        }
      }
      // Case 3: Moving a document within the same upload question
      else if (sourceQuestionId === targetQuestionId) {
        const targetDocument = this.listDocuments(sourceQuestion.answer)[
          oldIndex
        ];

        const sourceQuestionDocuments = this.listDocuments(
          sourceQuestion.answer,
        );
        sourceQuestionDocuments.splice(oldIndex, 1);
        sourceQuestionDocuments.splice(newIndex, 0, targetDocument);
        sourceQuestion.answer = '&!&' + sourceQuestionDocuments.join('&!&');

        await API.documentDropOff.answerQuestion({
          id: sourceQuestion.id,
          answer: sourceQuestion.answer,
          token: this.token,
        });
      }
      // Case 4: Moving a document from an upload question to another question
      else {
        const targetDocument = this.listDocuments(sourceQuestion.answer)[
          oldIndex
        ];

        const sourceQuestionDocuments = this.listDocuments(
          sourceQuestion.answer,
        ).filter(d => d !== targetDocument);

        sourceQuestion.answer =
          sourceQuestionDocuments.length === 0
            ? ''
            : '&!&' + sourceQuestionDocuments.join('&!&');

        const targetQuestionDocuments = this.listDocuments(
          targetQuestion.answer,
        );
        targetQuestionDocuments.splice(newIndex, 0, targetDocument);
        targetQuestion.answer = '&!&' + targetQuestionDocuments.join('&!&');

        await Promise.all([
          API.documentDropOff.answerQuestion({
            id: sourceQuestion.id,
            answer: sourceQuestion.answer,
            token: this.token,
          }),
          API.documentDropOff.answerQuestion({
            id: targetQuestion.id,
            answer: targetQuestion.answer,
            token: this.token,
          }),
        ]);
      }
    },

    async removeDocument(id) {
      try {
        const response = await API.extractedDocument.deleteExtractedDocument(
          id,
        );
        if (response.data.error) {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: `There was a problem removing the document.`,
          });
        } else {
          this.documentsUploaded = this.documentsUploaded.filter(d => d !== id);
        }
      } catch (e) {
        console.log(e);
      }
    },

    formatPreviewLink(document) {
      return document.source
        .split('/')
        .slice(3)
        .join('/');
    },

    formatReason(question, parentQuestion) {
      return `${this.currentUser.is_advisor ? 'Client' : 'You'} answered <b>'${
        question.trigger_for_follow_up_question
      }</b>' to<br/>'<b>${parentQuestion.text}</b>'`;
    },

    async addFile(e) {
      const documents = e.dataTransfer.files;

      if (documents) {
        this.documentsToImport.push(...documents);

        this.uploadForAutoMatching();
      }
    },

    async onDocumentsPicked(e) {
      const documents = e.target.files;
      if (documents) {
        this.documentsToImport.push(...documents);

        this.uploadForAutoMatching();
      }
    },

    pickDocuments() {
      const input = this.$refs.selectedDocuments;
      input.type = 'text';
      input.type = 'file';
      this.$refs.selectedDocuments.click();
    },

    async uploadForAutoMatching() {
      try {
        this.uploading = true;
        let path;
        if (isEmpty(this.currentQuestionnaire.document_path)) {
          path = '';
        } else {
          path = this.currentQuestionnaire.document_path
            .replace('{year}', this.currentQuestionnaire.tax_year)
            .replace('{section}', 'Bulk Uploads')
            .replace('{questionnaire_name}', this.currentQuestionnaire.name);
        }
        this.importDocumentsDialogOpened = false;
        let success = 0;

        for (const documentToImport of this.documentsToImport) {
          const trueExtension = await this.getTrueExtension(documentToImport);
          const currentExtension = this.getExtensionOfFilename(
            documentToImport.name,
          );
          const extensionsToCheck = ['pdf', 'png', 'gif', 'jpeg'];

          if (documentToImport.size === 0) {
            throw new Error(
              `We were unable to process some of uploaded files as they appear to be EMPTY. Kindly check those files and try uploading them again.`,
            );
          }

          if (
            extensionsToCheck.includes(currentExtension) &&
            trueExtension !== currentExtension
          ) {
            throw new Error(
              `We were unable to process some of uploaded files as they appear to be INVALID. Kindly check those files and try uploading them again.`,
            );
          }

          if (currentExtension === 'pdf') {
            if (await this.pdfHasPassword(documentToImport)) {
              throw new Error(
                `The PDFs you uploaded are password-protected. Please either remove the password or upload alternative files.`,
              );
            }
          }

          const {
            data: { id, upload_link },
          } = await API.documentDropOff.getTemporaryUploadLink({
            token: this.token,
          });

          await API.integration.uploadToDropbox(upload_link, documentToImport);

          const response = await API.documentDropOff.processDocument({
            id,
            documentName: documentToImport.name,
            documentType: 'dropped_off_document',
            questionnaireId: this.currentQuestionnaire.id,
            path: '/'.concat(path),
            requiresSplitting: documentToImport.requiresSplitting,
            token: this.token,
          });

          // posthog.capture('uploaded document', {
          //   source: 'questionnaire bulk upload',
          // });

          if (response.data.error) {
            this.$store.dispatch('application/setErrorNotification', {
              message: `There was a problem uploading ${documentToImport.name}.`,
            });
          } else if (response.data.is_duplicate) {
            this.$store.dispatch('application/setErrorNotification', {
              message: `The document ${documentToImport.name} has already been uploaded before. Thus, you don't need to upload it again.`,
            });
          } else {
            success += 1;
          }
        }

        if (this.documentsToImport.length === success) {
          this.$store.dispatch('application/setSuccessNotification', {
            message: `You have successfully uploaded ${
              this.documentsToImport.length
            } document${this.documentsToImport.length > 1 ? 's' : ''}!`,
          });
        }
        this.refreshQuestionnaireUntilDocumentsAreProcessed();
      } catch (e) {
        this.$store.dispatch('application/setErrorNotification', {
          message: has(e, 'message')
            ? get(e, 'message')
            : `There was a problem uploading the document${
                this.documentsToImport.length > 1 ? 's' : ''
              }.`,
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.documentsToImport = [];
        this.uploading = false;
        this.documentChecklistLoading = true;
      }
    },

    async completeQuestionnaire() {
      try {
        this.completionLoading = true;
        this.questionnaireCompletedConfirmationDialogOpened = false;
        this.currentQuestionnaire.completed = true;

        const response = await API.documentDropOff.submitQuestionnaire({
          token: this.token,
        });

        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
          console.log(response.data.error);
          this.completionLoading = false;
        } else {
          this.submitted = true;
          // posthog.capture('submitted questionnaire', {
          //   questionnaire_id: this.currentQuestionnaire.id,
          // });

          this.documentDropOffCompletionDialogOpened = true;

          this.completionLoading = false;
        }
      } catch (e) {
        console.log(e);

        try {
          if (e.response.status === 400) {
            await this.getQuestionnaireWithToken();

            if (this.currentQuestionnaire.completed) {
              this.documentDropOffCompletionDialogOpened = true;
            }

            this.completionLoading = false;
          } else {
            Sentry.captureMessage(
              'We have an unexpected error in DocumentDropOff.vue',
              { error: e },
            );
            console.log(e);
          }
        } catch (err) {
          Sentry.captureMessage(
            'We have an unexpected error in DocumentDropOff.vue',
            { error: err, originalError: e },
          );
          console.log(err);
        }
      }
    },

    async getQuestionnaireWithToken() {
      try {
        const response = await API.documentDropOff.getQuestionnaireWithToken({
          token: this.token,
        });
        if (response.data.error) {
          console.error(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message:
              'The link is expired or not valid. Please login to view the checklist.',
          });
          this.$router.push(`/signin?redirect=${this.questionnaireLink}`);
        } else {
          this.currentQuestionnaire = response.data;
        }
      } catch (e) {
        console.error(e);
        this.$store.dispatch('application/setErrorNotification', {
          message:
            'The link is expired or not valid. Please login to view the checklist.',
        });
        this.$router.push(`/signin?redirect=${this.questionnaireLink}`);
      } finally {
        this.loading = false;
      }
    },
    signIn() {
      const decoded = jwt_decode(this.token);
      if (decoded.email) {
        this.$router.push(
          `/signin?redirect=${
            this.$route.fullPath
          }&email=${decoded.email.replace('+', '%2b')}`,
        );
      } else {
        this.$router.push(`/signin?redirect=${this.$route.fullPath}`);
      }
    },
    async refreshQuestionnaireUntilDocumentsAreProcessed() {
      try {
        this.getQuestionnaireWithToken();
        // Wait 3 seconds before checking if everything has been processed and detailed name has been extracted
        this.sleep(3000).then(() => {
          if (
            this.currentQuestionnaire.extracted_documents.some(
              document =>
                moment(document.created_at) > moment().subtract(5, 'minutes') &&
                document.status === 'processing',
            )
          ) {
            this.documentChecklistLoading = true;
            console.log('refreshing documents');
            this.refreshQuestionnaireUntilDocumentsAreProcessed();
            this.refreshCount = 200;
          } else {
            if (this.refreshCount > 0) {
              this.documentChecklistLoading = false;
              this.refreshQuestionnaireUntilDocumentsAreProcessed();
              this.refreshCount -= 1;
            } else {
              this.refreshCount = 200;
            }
          }
        });
        console.log('checking documents');
      } catch (e) {
        console.log(e);
      }
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
  },

  async created() {
    this.loading = true;

    if (this.token) {
      const decoded = jwt_decode(this.token);
      const isSorabanToken = Boolean(decoded.iss === 'soraban');

      if (isSorabanToken) {
        await this.refreshQuestionnaireUntilDocumentsAreProcessed();
        if (this.currentQuestionnaire.completed) {
          this.documentDropOffCompletionDialogOpened = true;
        }
      } else {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem with the link. Please sign in.',
        });
        this.$router.push(`/signin?redirect=${this.questionnaireLink}`);
      }
    } else {
      this.$router.push(`/signin?redirect=${this.questionnaireLink}`);
    }
  },

  watch: {
    currentQuestionnaireOtherDocumentsCount: {
      handler(newValue, oldValue) {
        if (newValue > oldValue) {
          this.expandAdditionalAttachments();
        }
      },
    },
  },
};
</script>

<style lang="postcss" scoped>
.section-title {
  font-weight: 700;
  font-size: 20px;
}
.option-group {
  padding: 0px 10px 12px;
  margin: 0;
  border-top: 1px solid rgba(0, 0, 0, 0.1);

  & >>> .v-input {
    margin-top: 12px;
  }
  & >>> .default-input .v-input__slot {
    border: 1px solid #c9c9c9;
    border-radius: 3px;
  }
}
.file-item {
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  background: #fff;
  cursor: pointer;

  &:hover {
    background: #f1f1f1;
  }
}

.document-item {
  padding: 5px 0px;
}

.description {
  font-size: 15px;
}

.document-item-text {
  margin-top: 6px;
  font-size: 16px;

  & >>> p {
    margin-bottom: 8px;
  }
}

.drag-and-drop {
  height: 140px;
  border-radius: 4px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.2);

  & >>> i {
    color: rgba(0, 0, 0, 0.3);
  }

  & >>> span {
    color: rgba(0, 0, 0, 0.6);
  }

  & >>> span.clickable {
    cursor: pointer;
  }
}
.clickable-chip {
  & >>> .v-chip__content {
    cursor: pointer !important;
  }
}
.outlined-red {
  border: 1px solid #cb4039;
}
.drag-and-drop-text {
  font-size: 14px;
}
.max-size-text {
  font-size: 14px;
}
.bulk-item {
  padding: 8px 20px;
  align-items: center;
}

.file-name {
  font-size: 16px;
  font-weight: bold;
}

.send-questionnaire-dialog__block {
  padding: 24px 16px;

  background-color: var(--gray-25);
  border: 1px solid var(--gray-50) !important;
  border-radius: 4px;
  transition: all 120ms ease-in-out;
  min-height: 230px;
}

.send-questionnaire-dialog__block:hover {
  border-color: var(--gray-100) !important;
  background-color: var(--gray-100);
}

.left-container {
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 80vh;
  justify-content: center;
}
.option-group {
  padding: 0px 10px 12px;
  margin: 0;
  border-top: 1px solid rgba(0, 0, 0, 0.1);

  & >>> .v-input {
    margin-top: 12px;
  }
}

.expansion-panel {
  border: none;
  box-shadow: none;

  & >>> .v-expansion-panel__container {
    border: 1px solid rgba(0, 0, 0, 0.2) !important;
    border-radius: 4px;
    margin-bottom: 12px;
    overflow: hidden;
  }

  & >>> .v-expansion-panel__container.outlined-red {
    border: 1px solid red !important;
  }
}
</style>
